import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMergeRefs } from '@floating-ui/react';
import { IconButton } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { CancelSVGpath } from '@skatteetaten/ds-icons';
import { PopoverContext } from './PopoverContext/index.esm.js';
import './PopoverContent/styles.css';


const getPopoverColorDefault = ()=>'forest';
const getPopoverPositionDefault = ()=>'bottomStart';
const getPopoverRestoreFocusDefault = ()=>true;

var styles = {
    "popover": "PopoverContent-module_popover__1i7nF",
    "popover_forest": "PopoverContent-module_popover_forest__-WlLT",
    "popover_ochre": "PopoverContent-module_popover_ochre__C9YUJ",
    "popover_white": "PopoverContent-module_popover_white__gwTu5",
    "popoverContent": "PopoverContent-module_popoverContent__iaSZD",
    "popoverContentWrapper": "PopoverContent-module_popoverContentWrapper__bhEag",
    "popoverContentCloseButton": "PopoverContent-module_popoverContentCloseButton__IZs-q",
    "popoverArrow": "PopoverContent-module_popoverArrow__wCAG8",
    "popoverArrow_top": "PopoverContent-module_popoverArrow_top__TYOrF",
    "popoverArrow_bottom": "PopoverContent-module_popoverArrow_bottom__h891-",
    "popoverArrow_left": "PopoverContent-module_popoverArrow_left__GmPTL",
    "popoverArrow_right": "PopoverContent-module_popoverArrow_right__MPxU2"
};

/* eslint-disable react/forbid-dom-props */ const PopoverContent = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, children }, ref)=>{
    const { floatingData, interactions, arrowRef, isOpen, setIsOpen, color = getPopoverColorDefault(), shouldRestoreFocus = getPopoverRestoreFocusDefault(), onClose } = useContext(PopoverContext);
    const { refs, floatingStyles, placement, middlewareData } = floatingData;
    const { getFloatingProps } = interactions;
    const { t } = useTranslation('Shared', {
        i18n: dsI18n
    });
    const side = placement.split('-')[0];
    const colorClassName = styles[`popover_${color}`];
    const arrowPositionClassName = styles[`popoverArrow_${side}`];
    const mergedRef = useMergeRefs([
        refs.setFloating,
        ref
    ]);
    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right'
    }[side];
    if (!isOpen) {
        return null;
    }
    return /*#__PURE__*/ jsxs("div", {
        ...getFloatingProps(),
        ref: mergedRef,
        style: floatingStyles,
        id: id,
        lang: lang,
        "data-testid": dataTestId,
        className: `${styles.popover} ${colorClassName} ${className}`.trim(),
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: styles.popoverContent,
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: styles.popoverContentWrapper,
                        children: children
                    }),
                    /*#__PURE__*/ jsx(IconButton, {
                        className: styles.popoverContentCloseButton,
                        svgPath: CancelSVGpath,
                        title: t('shared.Close'),
                        onClick: ()=>{
                            onClose?.();
                            setIsOpen(false);
                            if (shouldRestoreFocus) {
                                refs.domReference.current?.focus();
                            }
                        }
                    })
                ]
            }),
            /*#__PURE__*/ jsx("div", {
                ref: arrowRef,
                style: {
                    left: middlewareData.arrow?.x,
                    top: middlewareData.arrow?.y,
                    ...staticSide ? {
                        [staticSide]: `-${(arrowRef.current?.offsetWidth ?? 0) / 2}px`
                    } : {}
                },
                className: `${styles.popoverArrow} ${arrowPositionClassName}`.trim()
            })
        ]
    });
});
PopoverContent.displayName = 'PopoverContent';

export { PopoverContent as P, getPopoverPositionDefault as a, getPopoverRestoreFocusDefault as b, getPopoverColorDefault as g };
